/**
 * postMessage bridge to the iframe parent for lead-tracking.
 *
 * The parent embeds Quotr with `?parentOrigin=https://parent.example.com` on
 * the iframe URL. On startup we capture and validate that origin against the
 * allowlist below, persist it in sessionStorage so it survives in-app SPA
 * navigations, and use it as the (mandatory) `targetOrigin` for every
 * postMessage call. If no valid origin is captured, or we're not actually
 * iframed, every notify call is a silent no-op.
 *
 * Why a strict allowlist + exact targetOrigin: postMessage with `'*'` would
 * leak event data to any embedder, including a malicious one that iframes us
 * after CSP is bypassed (e.g. a browser extension). The allowlist lives in
 * `config/iframe-parents.json` and must stay in sync with the CSP
 * `frame-ancestors` list in `staticwebapp.config.json`.
 */

import iframeParentsConfig from '@/../config/iframe-parents.json';

const PARENT_ORIGIN_QUERY_PARAM = 'parentOrigin';
const SESSION_STORAGE_KEY = 'quotr.parentOrigin';
const MESSAGE_SOURCE = 'quotr';

const ALLOWED_PARENT_ORIGINS: ReadonlySet<string> = new Set(
	iframeParentsConfig.allowedParentOrigins
);

/**
 * Conversion signals reported to the iframe parent for lead-tracking:
 * - `upload`         — the customer uploaded a policy document
 * - `quotes_viewed`  — the customer reached the page where quotes are shown
 * - `contact_request`— the customer submitted the contact / advisor form
 * - `conversion`     — the customer completed an application (activation in hand)
 */
export type ParentMessageEvent = 'upload' | 'quotes_viewed' | 'contact_request' | 'conversion';

export interface ParentMessage {
	source: typeof MESSAGE_SOURCE;
	event: ParentMessageEvent;
	[key: string]: unknown;
}

const safeSessionStorage = (): Storage | null => {
	try {
		return window.sessionStorage;
	} catch {
		// Storage can throw in some sandboxed iframes / privacy modes.
		return null;
	}
};

const normalizeOrigin = (raw: string): string | null => {
	try {
		const url = new URL(raw);
		// Strip any path/search/hash — postMessage targetOrigin compares scheme
		// + host + port only, and we want the stored value to match what
		// `event.origin` looks like on the receiving side.
		return `${url.protocol}//${url.host}`;
	} catch {
		return null;
	}
};

/**
 * Reads `?parentOrigin=` from the current URL and persists it if it's on the
 * allowlist. Safe to call multiple times — later calls without the query
 * param leave any previously-captured origin in place, so SPA navigations
 * that drop the query string don't lose the tracking target.
 */
export const captureParentOrigin = (): void => {
	if (typeof window === 'undefined') return;

	const params = new URLSearchParams(window.location.search);
	const raw = params.get(PARENT_ORIGIN_QUERY_PARAM);
	if (!raw) return;

	const origin = normalizeOrigin(raw);
	if (!origin) {
		console.warn(`[parentMessenger] Ignoring malformed parentOrigin: ${raw}`);
		return;
	}
	if (!ALLOWED_PARENT_ORIGINS.has(origin)) {
		console.warn(`[parentMessenger] Ignoring parentOrigin not on allowlist: ${origin}`);
		return;
	}

	try {
		safeSessionStorage()?.setItem(SESSION_STORAGE_KEY, origin);
	} catch {
		// setItem can throw in private/restricted modes (QuotaExceeded, security
		// errors). Swallow — notifyParent will no-op later if the origin never
		// made it into storage.
	}
};

export const getParentOrigin = (): string | null => {
	try {
		return safeSessionStorage()?.getItem(SESSION_STORAGE_KEY) ?? null;
	} catch {
		return null;
	}
};

/**
 * Sends a `{ source: 'quotr', event, ...payload }` message to the captured
 * parent origin. No-op when we're not iframed, no parent origin was captured,
 * or the captured origin somehow fell off the allowlist between capture and
 * send. Errors are swallowed — lead tracking must never break the funnel.
 */
export const notifyParent = (
	event: ParentMessageEvent,
	payload?: Record<string, unknown>
): void => {
	if (typeof window === 'undefined') return;
	if (window.parent === window) return;

	const targetOrigin = getParentOrigin();
	if (!targetOrigin) return;
	if (!ALLOWED_PARENT_ORIGINS.has(targetOrigin)) return;

	// Spread payload first so the reserved protocol fields (`source`, `event`)
	// always win — a caller passing `{ source: 'spoofed' }` must not be able to
	// break the parent-side filter contract.
	const message: ParentMessage = {
		...payload,
		source: MESSAGE_SOURCE,
		event
	};

	try {
		window.parent.postMessage(message, targetOrigin);
	} catch (err) {
		console.warn('[parentMessenger] postMessage failed', err);
	}
};
